import { CreateTopicRequest, GetPointResponse, TagCategoryDataType, TopicCategory, UserTag } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { getPointsWithTags, numCategoryPoints } from '../helpers/tags-categories';

import { StateController } from '../helpers/state/state-management/state-controller';
import { createTopicRequestFromTopic } from '../helpers/api/topics-api';
import { detectMobileViewport } from '../helpers/device';
import { getMainPage } from '../helpers/page-name';
import { isDefined } from '../helpers/utils';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('edit-categories-tags')
export class EditCategoriesTags extends LitElement {
    amplitude_callsite: string = 'Category Item';



    @property()
    dataType: TagCategoryDataType = TagCategoryDataType.Category;

    @property()
    data?: TopicCategory | UserTag;

    @property()
    showSave: boolean = true;

    @state()
    numPoints?: number;

    @state()
    tag?: UserTag;

    @state()
    category?: TopicCategory;

    @state()
    updating: boolean = false;

    @state()
    createTopicRequest: CreateTopicRequest = createTopicRequestFromTopic(topicData.topic);

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointState = new StateController(this, pointCache);

    updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'dataType' || propName ==='data') {
                if (this.dataType === TagCategoryDataType.Category) {
                    this.category = this.data as TopicCategory;
                    if (isDefined(this.category?._id?.$oid)) {
                        this.numPoints = numCategoryPoints(this.category?._id?.$oid ?? '', topicData.categories, pointCache.allPoints);
                    }
                }
                else if (this.dataType === TagCategoryDataType.Tag || this.dataType === TagCategoryDataType.TagGroup) {
                    this.tag = this.data as UserTag;
                }
            }
        });
    }

    async disableSave(e: CustomEvent) {
       if (e.detail.showSubTagInput === true) {
            this.showSave = false;
        } else if (e.detail.showSubTagInput === false) {
            this.showSave = true;
        }
    }

    render() {
        return html`
            <div class="edit__container">
                <span class="edit__content">
                    ${this.updating ? html`
                        <loading-element loadingString="Updating">
                        </loading-element>
                    ` : html`
                        ${this.dataType === TagCategoryDataType.Category
                            ? html`
                                <category-form
                                    .categoryID=${this.category?._id?.$oid}
                                ></category-form>`
                            : ''
                        }
                        ${this.dataType === TagCategoryDataType.Tag
                            ? html`<tag-form .tag=${this.tag}></tag-form>`
                            : ''
                        }
                        ${this.dataType === TagCategoryDataType.TagGroup
                            ? html`<tag-form .tag=${this.tag} .showSubTags=${true} @show-subtag-input=${this.disableSave} @points-updating=${(e: CustomEvent) => {this.updating = e.detail.pointsUpdating;}}></tag-form>`
                            : ''
                        }
                        <div class="edit__actions">
                            ${this.showSave ?
                                html`

                            <form-button size="md" backgroundColor="var(--main-invalid-color)" class="${this.showSave ? 'edit__delete' : 'edit__delete--only'}" @click="${this.delete}">
                                Delete ${this.dataType === TagCategoryDataType.Category ? 'Category' : this.dataType === TagCategoryDataType.Tag ? 'Tag' : 'Group'}
                            </form-button>
                            ${detectMobileViewport() ? '' : html`
                                <form-button variant="outline" size="md" class="edit__save"  @click="${this.closeSelf}">
                                    Close
                                </form-button>
                                `}
                            `
                            : ''
                            }
                        </div>
                        ${this.showSave ?
                            html``
                            : ''
                        }
                    `}
                </span>
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    protected closeSelf() {
        this.dispatchEvent(new CustomEvent('closeDrawer', {
            bubbles: true,
            composed: true,
        }));

        // this is for mobile - coming from a diff flow
        this.dispatchEvent(new CustomEvent('on-close', {
            bubbles: true,
            composed: true,
        }));
    }

    private async delete() {
        if (this.dataType === TagCategoryDataType.Category) {
            if (this.category === undefined) {
                return;
            }
            try {
                await topicData.checkForCategoryExceptions(this.category);
            } catch (e) {
                return;
            }
        }

        // ask for confirmation
        if (this.dataType === TagCategoryDataType.Category) {
            // If there is only one category, then it can't be deleted.
            if (topicData.categories.length <= 1 || !this.category) {
                getMainPage().errorToastElement.showToast('You must have at least one category in your topic.');
                return;
            }

            const category_id = this.category._id?.$oid;

            if(!category_id) {
                console.error('Category ID is undefined');
                getMainPage().errorToastElement.showToast('There was an error deleting this category or contact support. Please try again.');
                return;
            }

            this.numPoints = numCategoryPoints(category_id, topicData.categories, pointCache.allPoints);
            if (!isDefined(this.numPoints)) {
                getMainPage().errorToastElement.showToast('There was an error deleting this category or contact support. Please try again.');
                return;
            }
            if (this.numPoints !== 0 && !confirm(`Are you sure you want to delete this category? It will also delete the ${String(this.numPoints)} points associated with it. This cannot be undone.`)) {
                return;
            }
        } else if (this.dataType === TagCategoryDataType.Tag || this.dataType === TagCategoryDataType.TagGroup) {
            if (!confirm(`Are you sure you want to delete this ${this.dataType === TagCategoryDataType.Tag ? 'tag' : 'group'}? This action cannot be undone and will remove the tag from all points that have it.`)) {
                return;
            }
        }


        this.updating = true;

        if (this.dataType === TagCategoryDataType.Category) {
            if (topicData.categories.length <= 1 || this.category?._id === undefined) {
                return;
            }

            await topicData.deleteCategory(this.category._id.$oid, this.amplitude_callsite);
        }

        if (this.dataType === TagCategoryDataType.Tag || this.dataType === TagCategoryDataType.TagGroup) {

            if (this.tag?._id === undefined) {
                return;
            }
            await topicData.deleteTag(this.tag._id.$oid, this.amplitude_callsite);

            if (isDefined(this.tag)) {
                let pointsWithTag: GetPointResponse[] = [];
                if (this.dataType === TagCategoryDataType.TagGroup && this.tag?.tags !== undefined) {
                    pointsWithTag = getPointsWithTags(this.tag.tags, pointCache.allPoints);
                } else {
                    pointsWithTag = getPointsWithTags([this.tag], pointCache.allPoints);
                }
                this.updating = true;
                await pointCache.syncPointTags(pointsWithTag, topicData.topLevelUserTags);
            }
        }
        this.updating = false;
        this.tag = undefined;
        this.category = undefined;
        this.closeSelf();
    }

    static get styles() {
        return css`
            .edit__container {
                width: 100%;
                position: relative;
            }

            .edit__content {
                padding-top: 12px;
                width: 100%;
            }

            .edit__save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .edit__delete {
                --mdc-theme-primary: var(--main-invalid-color);
            }

            .edit__close {
                --mdc-icon-size: 24px;
                --mdc-icon-button-size: 18px;
                padding: 4px 2px 2px 2px;
                position: absolute;
                top: -1rem;
                right: 0;
            }

            .edit__actions {
                display: flex;
                gap: 0.5rem;
                margin-bottom: 1.5rem;
                align-items: flex-end;
                justify-content: space-between;
            }
        `;
    }
}